import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, selectTenant, clearError } from '../store/slices/authSlice';
import api from '../api';
import { useDomainParams } from '../hooks/useDomainParams';

function PAASAdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedPAAS, setSelectedPAAS] = useState('');
  const [paasList, setPaasList] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user } = useSelector((state) => state.auth);
  const { paasId: domainPaasId, branding, isLoading: domainLoading, error: domainError } = useDomainParams();

  useEffect(() => {
    const fetchPAAS = async () => {
      try {
        const res = await api.get('/paas/public');
        setPaasList(res.data.data || []);
      } catch (err) {
        console.error('Failed to fetch PAAS list:', err);
      }
    };
    fetchPAAS();
  }, []);

  useEffect(() => {
    if (domainPaasId && paasList.length > 0) {
      const exists = paasList.some(p => p.paasId === domainPaasId);
      if (exists) {
        setSelectedPAAS(domainPaasId);
      }
    }
  }, [domainPaasId, paasList]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'superadmin') {
      navigate('/super-admin/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const getPaasId = (paas) => paas.paasId;
  const getPaasName = (paas) => paas.name;
  const selectedPAASData = paasList.find(p => getPaasId(p) === selectedPAAS);
  const marketPlaceName = selectedPAASData?.name || branding?.title || 'Platform';

  const primaryColor = branding?.primaryColor || '#059669';
  const logoUrl = branding?.logo;

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());

    const result = await dispatch(login({ email, password, paasId: selectedPAAS }));

    if (login.fulfilled.match(result)) {
      const loggedInUser = result.payload.user;

      if (loggedInUser?.role === 'superadmin') {
        navigate('/super-admin/dashboard');
      } else if (selectedPAAS) {
        navigate(`/paas-admin/${selectedPAAS}`);
      }
    }
  };

  if (domainError) {
    return (
      <div className="login-page" style={styles.page}>
        <div className="login-card" style={styles.card}>
          <div style={styles.errorBanner}>
            <span style={styles.errorIcon}>⚠️</span>
            <h2 style={styles.errorTitle}>Invalid PaaS Domain Configuration</h2>
            <p style={styles.errorText}>{domainError}</p>
            <a href="/" style={styles.homeLink}>← Return to Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ ...styles.page, background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          {logoUrl ? (
            <img src={logoUrl} alt="PAAS Logo" style={styles.paasLogo} />
          ) : (
            <div style={styles.logo}>🏢</div>
          )}
          <h1 style={styles.title}>{marketPlaceName} Admin</h1>
          <p style={styles.subtitle}>Platform Administration</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Select Platform</label>
            <select
              value={selectedPAAS}
              onChange={(e) => setSelectedPAAS(e.target.value)}
              style={styles.select}
              required
              disabled={domainLoading}
            >
              <option value="">{domainLoading ? 'Loading...' : 'Select a platform...'}</option>
              {paasList.map(paas => (
                <option key={getPaasId(paas)} value={getPaasId(paas)}>
                  {getPaasName(paas)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@platform.com"
              style={styles.input}
              required
            />
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={styles.input}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || (!selectedPAAS && !domainPaasId)}
            style={{...styles.button, background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`, opacity: (!selectedPAAS && !domainPaasId) ? 0.6 : 1}}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.footer}>
          <a href="/login" style={styles.link}>← Back to Main Login</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  paasLogo: {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    margin: '0 auto 12px auto',
    objectFit: 'contain',
    display: 'block',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
  },
  select: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    backgroundColor: 'white',
  },
  button: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
  },
  link: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
  },
  errorBanner: {
    textAlign: 'center',
    padding: '20px',
  },
  errorIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '8px',
  },
  errorText: {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '20px',
  },
  homeLink: {
    color: '#4a90d9',
    textDecoration: 'none',
    fontSize: '14px',
  },
};

export default PAASAdminLogin;